import React, { useState, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import parse, { domToReact, attributesToProps } from 'html-react-parser'
import { useToasts } from 'react-toast-notifications'
import { getNotifications } from '_redux/modules/user'
import { formatUnixToDate } from '_utils/formatNumber'

const parseOptions = {
  replace: (domNode) => {
    if (domNode.name === 'a') {
      const props = attributesToProps(domNode.attribs || {})
      return (
        <a
          {...props}
          className={`notif-link notif-order-ref ${props.className || ''}`.trim()}
          onClick={(e) => {
            e.stopPropagation()
            if (props.onClick) props.onClick(e)
          }}
        >
          {domToReact(domNode.children)}
        </a>
      )
    }
    return undefined
  },
}

const getNotificationCategory = (item) => {
  if (item.type) return item.type
  const content = `${item.title || ''} ${item.message || ''} ${item.subMessage || ''}`.toLowerCase()
  if (content.includes('collect') || content.includes('pickup') || content.includes('delivery')) {
    return 'collect'
  }
  if (content.includes('payment') || content.includes('paid') || content.includes('uen') || content.includes('price')) {
    return 'payment'
  }
  if (content.includes('event') || content.includes('broadcast') || content.includes('closing')) {
    return 'event'
  }
  return 'system'
}

const renderIcon = (type) => {
  switch (type) {
    case 'collect':
      return (
        <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
          <path d='M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z' />
          <line x1='3' y1='6' x2='21' y2='6' />
          <path d='M16 10a4 4 0 0 1-8 0' />
        </svg>
      )
    case 'payment':
      return (
        <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
          <rect x='1' y='4' width='22' height='16' rx='2' ry='2' />
          <line x1='1' y1='10' x2='23' y2='10' />
        </svg>
      )
    case 'event':
      return (
        <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
          <rect x='3' y='4' width='18' height='18' rx='2' />
          <line x1='16' y1='2' x2='16' y2='6' />
          <line x1='8' y1='2' x2='8' y2='6' />
          <line x1='3' y1='10' x2='21' y2='10' />
        </svg>
      )
    case 'system':
    default:
      return (
        <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
          <circle cx='12' cy='12' r='10' />
          <line x1='12' y1='8' x2='12' y2='12' />
          <line x1='12' y1='16' x2='12.01' y2='16' />
        </svg>
      )
  }
}

const NotificationsTab = ({ onUnreadCountChange }) => {
  const dispatch = useDispatch()
  const { addToast } = useToasts()
  const { notifications: notificationsRedux = [] } = useSelector((state) => state.user || {})

  const [rawList, setRawList] = useState([])
  const [readIds, setReadIds] = useState(() => {
    try {
      const saved = localStorage.getItem('cobee_read_notifs')
      return saved ? JSON.parse(saved) : []
    } catch (e) {
      return []
    }
  })

  useEffect(() => {
    dispatch(getNotifications)
  }, [dispatch])

  useEffect(() => {
    setRawList(notificationsRedux || [])
  }, [notificationsRedux])

  const activeNotifications = rawList

  const unreadCount = useMemo(
    () => activeNotifications.filter((item) => !readIds.includes(item.id)).length,
    [activeNotifications, readIds]
  )

  useEffect(() => {
    if (onUnreadCountChange) {
      onUnreadCountChange(unreadCount)
    }
  }, [unreadCount, onUnreadCountChange])

  const handleMarkAllRead = () => {
    const allIds = activeNotifications.map((item) => item.id)
    const newReadIds = Array.from(new Set([...readIds, ...allIds]))
    setReadIds(newReadIds)
    localStorage.setItem('cobee_read_notifs', JSON.stringify(newReadIds))
    addToast('All notifications marked as read', { appearance: 'success', autoDismiss: true })
  }

  const handleItemClick = (id) => {
    if (!readIds.includes(id)) {
      const newReadIds = [...readIds, id]
      setReadIds(newReadIds)
      localStorage.setItem('cobee_read_notifs', JSON.stringify(newReadIds))
    }
  }

  const formatDate = (createdAt) => {
    if (!createdAt) return ''
    const timestamp =
      typeof createdAt === 'number' && createdAt > 10000000000
        ? Math.floor(createdAt / 1000)
        : createdAt
    return formatUnixToDate(timestamp)
  }

  return (
    <div className='tab-notifications-view'>
      <div className='section-card'>
        <div className='section-hd'>
          <div className='section-title'>
            <svg
              width='15'
              height='15'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <path d='M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9' />
              <path d='M13.73 21a2 2 0 0 1-3.46 0' />
            </svg>
            Notifications
          </div>
          <button
            type='button'
            className='section-action'
            onClick={handleMarkAllRead}
            disabled={unreadCount === 0}
          >
            Mark all read
          </button>
        </div>

        <div className='notif-list-container'>
          {activeNotifications.length === 0 ? (
            <div className='notif-empty'>
              <div className='empty-icon'>
                <svg width='36' height='36' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5'>
                  <path d='M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9' />
                  <path d='M13.73 21a2 2 0 0 1-3.46 0' />
                </svg>
              </div>
              <p>No notifications at the moment</p>
            </div>
          ) : (
            activeNotifications.map((notif) => {
              const isUnread = !readIds.includes(notif.id)
              const category = getNotificationCategory(notif)
              const dateText = formatDate(notif.createdAt)
              const sender = notif.senderName || 'System'

              return (
                <div
                  key={notif.id}
                  id={`notif-${notif.id}`}
                  className={`notif-item ${isUnread ? 'unread' : ''}`}
                  onClick={() => handleItemClick(notif.id)}
                >
                  <div className={`notif-icon ${category}`}>{renderIcon(category)}</div>

                  <div className='notif-body'>
                    {dateText && <div className='notif-date'>{dateText}</div>}
                    <div className='notif-text'>
                      {notif.message ? parse(notif.message, parseOptions) : notif.title || ''}
                    </div>
                    {notif.subMessage && (
                      <div className='notif-sub'>{parse(notif.subMessage, parseOptions)}</div>
                    )}
                  </div>

                  <div className='notif-tag'>{sender}</div>
                </div>
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}

export default NotificationsTab
